"use client";

import { useState } from "react";
import Link from "next/link";
import { PricingToggle, type BillingPeriod } from "@/components/marketing/pricing-toggle";
import { freeJobPackHref } from "@/lib/marketing/free-job-pack";
import PricingCta from "./pricing-cta";

interface Tier {
  name: string;
  price: string;
  period: string;
  description: string;
  originalPrice: string | null;
  founderNote: string | null;
  features: string[];
  featuresExcluded?: string[];
  featuresExtra?: string[];
  cta: "free" | "monthly" | "annual" | "business";
  highlight: boolean;
  badge?: string;
  valueAnchor?: string;
}

const freeTier: Tier = {
  name: "Starter",
  price: "\u00a30",
  period: "",
  description: "Job one is free: your first RAMS, CPP and site induction, with no card required.",
  originalPrice: null,
  founderNote: null,
  features: [
    "1 real job only",
    "First RAMS, CPP, site induction and method statement workflow",
    "Standard hazard library for that project",
    "Review and edit before use",
    "Watermarked PDFs",
  ],
  featuresExcluded: [
    "Business branding and logo",
    "COSHH assessments",
    "Worker cert tracking and expiry alerts",
    "Site diary, permits and incident log",
  ],
  cta: "free",
  highlight: false,
};

const proMonthly: Tier = {
  name: "Pro Monthly",
  price: "\u00a339",
  period: "/month",
  description: "For job two, repeat work and firms using this as their proper paperwork system.",
  originalPrice: null,
  founderNote: "Founding pricing. Keep this price while your subscription stays active.",
  valueAnchor: "Upgrade when you need job two",
  features: [
    "Unlimited projects and documents",
    "Professional PDFs with your logo and no watermark",
    "COSHH assessments with one-click substance lookup",
    "Worker cert tracking with expiry alerts",
    "Document checks for vague wording before download",
    "Site diary, permits to work and incident log",
  ],
  featuresExtra: [
    "RAMS, CPP, site inductions and method statements as often as you need",
    "Upload a manufacturer data sheet and extract the safety information",
    "Subcontractor tracking for insurance and RAMS expiry dates",
    "Toolbox talks with ready-made topics and sign-off records",
    "Upload an existing pre-construction plan and build the project from it",
    "Clone previous jobs to reuse sites, contacts and setups",
    "F10 HSE notification checks",
    "Download all your data anytime",
    "Ask a safety question in the app when you are stuck",
    "Integrations with Google Drive, Xero and Zapier",
    "Cert expiry alerts for your workers and subcontractors",
    "Audit-ready compliance pack for any inspector visit",
  ],
  cta: "monthly",
  highlight: false,
};

const proAnnual: Tier = {
  name: "Pro Annual",
  price: "\u00a3360",
  period: "/year",
  description: "The same Pro plan, paid yearly for firms with regular site work.",
  originalPrice: null,
  founderNote: "Founding pricing. Keep this price while your subscription stays active.",
  valueAnchor: "Equivalent to \u00a330/month",
  features: [
    "All Pro Monthly features",
    "Unlimited projects and documents",
    "Professional PDFs with your logo and no watermark",
    "COSHH, cert tracking, diary, permits, incidents and toolbox talks",
    "Save \u00a3108 compared with paying monthly",
  ],
  cta: "annual",
  highlight: false,
  badge: "Save \u00a3108/year",
};

const businessTier: Tier = {
  name: "Business",
  price: "\u00a3199",
  period: "/month",
  description: "For builder-run firms, including 10–50-person teams, that need shared office control and live site operations around their CDM documents.",
  originalPrice: null,
  founderNote: null,
  features: [
    "Everything in Pro",
    "Up to 10 internal team logins",
    "Up to 50 active site portal users",
    "Site Supervisor logins scoped to assigned sites",
    "Optional 4-digit PIN for shared-link document sign-off",
    "Site files, drawings and procedures portal",
    "Site attendance and live check-in view",
    "Document review status and history",
    "Subcontractor evidence records and review",
    "Site inspections and corrective actions",
    "Shareable update links and email notifications",
    "Slack Site Ready for readiness checks in Slack",
    "Business support for rollout questions",
  ],
  cta: "business",
  highlight: false,
  badge: "Best for 10–50-person teams",
};

const featureLinks: Record<string, string> = {
  "RAMS, CPP, site inductions and method statements as often as you need":
    "/features/rams",
  "COSHH assessments with one-click substance lookup": "/features/coshh",
  "Worker cert tracking with expiry alerts": "/features/worker-tracking",
  "Site diary, permits to work and incident log": "/features/site-management",
  "Upload an existing pre-construction plan and build the project from it":
    "/features/ai-tools",
  "Integrations with Google Drive, Xero and Zapier": "/integrations",
  "Slack Site Ready for readiness checks in Slack": "/features/slack-site-ready",
  "Optional 4-digit PIN for shared-link document sign-off":
    "/help/workers-and-sign-off/worker-signoff-pins",
};

function TierCard({ tier, selected }: { tier: Tier; selected: boolean }) {
  return (
    <div
      id={tier.cta}
      className={`relative scroll-mt-28 rounded-2xl p-8 text-left ${
        selected ? "sm:p-10" : ""
      } ${
        tier.cta === "free"
          ? "border border-border bg-surface"
          : tier.cta === "business"
            ? "border border-navy/30 ring-1 ring-navy/10 bg-surface"
            : "border-2 border-accent bg-accent-light/30 ring-2 ring-accent/20 shadow-lg"
      }`}
    >
      {tier.cta === "business" && tier.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white">
            {tier.badge}
          </span>
        </div>
      )}
      {selected && tier.cta !== "free" && tier.cta !== "business" && (
        <div className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap">
          <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-white">
            Recommended
          </span>
          {tier.badge && (
            <span className="rounded-full bg-done px-3 py-1 text-xs font-bold text-white">
              {tier.badge}
            </span>
          )}
        </div>
      )}
      {tier.badge && !selected && tier.cta !== "business" && (
        <div className="absolute -top-3 right-6">
          <span className="rounded-full bg-done px-3 py-1 text-xs font-bold text-white">
            {tier.badge}
          </span>
        </div>
      )}
      <h3 className="text-lg font-bold text-navy">{tier.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted">
        {tier.description}
      </p>

      <p className="mt-4">
        {tier.originalPrice && (
          <span className="mr-2 text-2xl font-bold text-text-secondary line-through">
            {tier.originalPrice}
          </span>
        )}
        <span className="text-4xl font-extrabold text-navy">{tier.price}</span>
        {tier.period && (
          <span className="text-sm text-muted">{tier.period}</span>
        )}
      </p>
      {tier.valueAnchor && (
        <p className="mt-2">
          <span className="inline-block rounded-md bg-done-bg px-2 py-1 text-xs font-medium text-done">
            {tier.valueAnchor}
          </span>
        </p>
      )}
      {tier.founderNote && (
        <p className="mt-1 text-sm font-semibold text-brand-700">
          {tier.founderNote}
        </p>
      )}

      <ul className="mt-6 space-y-3">
        {tier.features.map((f) => {
          const href = featureLinks[f];
          return (
            <li
              key={f}
              className="flex items-start gap-2 text-sm text-muted"
            >
              <svg
                className={`mt-0.5 h-4 w-4 flex-shrink-0 ${tier.cta === "business" ? "text-navy" : "text-accent"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {href ? (
                <Link
                  href={href}
                  className="hover:text-accent-dark hover:underline"
                >
                  {f}
                </Link>
              ) : (
                f
              )}
            </li>
          );
        })}
      </ul>

      {tier.featuresExcluded && tier.featuresExcluded.length > 0 && (
        <ul className="mt-6 space-y-3 border-t border-border pt-5">
          {tier.featuresExcluded.map((f, i) => (
            <li
              key={f}
              className={`flex items-start gap-2 text-sm text-slate-400${i === 0 ? " border-t border-border pt-3" : ""}`}
            >
              <svg
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {f}
            </li>
          ))}
        </ul>
      )}

      {tier.featuresExtra && tier.featuresExtra.length > 0 && (
        <details className="group mt-4">
          <summary className="cursor-pointer list-none text-sm font-semibold text-brand-700 hover:text-accent-dark">
            <span className="group-open:hidden">+ 12 more features</span>
            <span className="hidden group-open:inline">Show fewer</span>
          </summary>
          <ul className="mt-3 space-y-3">
            {tier.featuresExtra.map((f) => {
              const href = featureLinks[f];
              return (
                <li
                  key={f}
                  className="flex items-start gap-2 text-sm text-muted"
                >
                  <svg
                    className={`mt-0.5 h-4 w-4 flex-shrink-0 ${tier.cta === "business" ? "text-navy" : "text-accent"}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {href ? (
                    <Link
                      href={href}
                      className="hover:text-accent-dark hover:underline"
                    >
                      {f}
                    </Link>
                  ) : (
                    f
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}

      <div className="mt-8">
        {tier.cta === "free" ? (
          <>
            <Link
              href={freeJobPackHref({ utmContent: "pricing_starter_card" })}
              className="block min-h-[44px] w-full rounded-lg border-2 border-navy bg-transparent px-4 py-3 text-center text-sm font-semibold text-navy transition-colors hover:bg-navy/5"
            >
              Create my free job pack
            </Link>
            <p className="mt-2 text-center text-xs text-muted">
              No card. Upgrade to Pro for job two.
            </p>
          </>
        ) : tier.cta === "business" ? (
          <PricingCta plan="business" highlight={tier.highlight} />
        ) : (
          <PricingCta plan={tier.cta} highlight={tier.highlight} />
        )}
      </div>
    </div>
  );
}

export default function PricingCards() {
  // Default to monthly: leads with the lower upfront commitment (£39/mo) to
  // reduce first-time-upgrade friction (brief Rec 3). Annual stays one tap away
  // and keeps its "Save £108/year" best-value badge. NOTE: this reverses the
  // earlier P30-02 "annual is the visual default" decision and trades headline
  // LTV for a lower commitment barrier — revert this one line + the toggle
  // default below to restore annual-first.
  const [period, setPeriod] = useState<BillingPeriod>("monthly");

  return (
    <div
      className="mt-10 grid gap-6 sm:grid-cols-2 lg:mx-auto lg:max-w-5xl lg:grid-cols-3"
      data-testid="pricing-cards"
    >
      <TierCard tier={freeTier} selected={false} />
      <div id="pro" className="scroll-mt-24">
        <div className="mb-5 flex justify-center">
          <div className="w-full max-w-sm">
            <PricingToggle defaultPeriod="monthly" onChange={setPeriod} />
          </div>
        </div>
        <TierCard
          key={period}
          tier={period === "annual" ? proAnnual : proMonthly}
          selected={true}
        />
      </div>
      <TierCard tier={businessTier} selected={false} />
    </div>
  );
}
